import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, Plus, FileSpreadsheet, Layers, Building2, Pencil, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import ImportDialog from '@/components/ImportDialog';
import { KONUM_ADI, cocuklar } from '@/lib/konum';

const IMPORT_SUTUNLARI = [
  { key: 'etap', label: 'Etap', render: (r) => r.etap || '—' },
  { key: 'blok', label: 'Blok / Bölge', render: (r) => r.blok || '—' },
  { key: 'kat', label: 'Kat', render: (r) => r.kat || '—' },
  { key: 'bolum', label: 'Bağımsız Bölüm', render: (r) => r.bolum || '—' },
  { key: 'mahal', label: 'Mahal', render: (r) => r.mahal || '—' },
  { key: 'durum', label: 'Durum', render: (r) => r.durum || '—' },
];

const pasifRozet = <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#7a3535]/10 text-[#7a3535]">Pasif</span>;

export default function SiteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [konumlar, setKonumlar] = useState([]);
  const [pasifGoster, setPasifGoster] = useState(false);
  // {mod: 'ekle'|'duzenle', tur, parent_id?, konum?, ad}
  const [form, setForm] = useState(null);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [excelAcik, setExcelAcik] = useState(false);

  const yukle = useCallback(async () => {
    try {
      const [s, k] = await Promise.all([
        api.get('/insaat/sites'), api.get(`/insaat/sites/${id}/konumlar`),
      ]);
      setSite((s.data || []).find((x) => x.id === id) || null);
      setKonumlar(k.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Şantiye yüklenemedi');
    }
  }, [id]);

  useEffect(() => { yukle(); }, [yukle]);

  // Ağaç köklerden gezildiği için pasif bir düğüm gizlenince altı da gizlenir.
  const gorunen = pasifGoster ? konumlar : konumlar.filter((k) => k.is_active);
  const pasifSayisi = konumlar.filter((k) => !k.is_active).length;
  const aktifSay = (tur) => konumlar.filter((k) => k.is_active && k.tur === tur).length;

  const ekle = (tur, parentId = null) => setForm({ mod: 'ekle', tur, parent_id: parentId, ad: '' });
  const duzenle = (k) => setForm({ mod: 'duzenle', tur: k.tur, konum: k, ad: k.ad });

  const kaydet = async (e) => {
    e?.preventDefault();
    const ad = (form.ad || '').trim();
    if (!ad) return;
    setKaydediliyor(true);
    try {
      if (form.mod === 'ekle') {
        const r = await api.post(`/insaat/sites/${id}/konumlar`, { tur: form.tur, ad, parent_id: form.parent_id });
        toast.success({
          yeni: `${KONUM_ADI[form.tur]} eklendi`,
          yeniden_aktif: 'Bu adda pasif bir kayıt vardı, yeniden aktif edildi',
          mevcut: 'Bu adda bir kayıt zaten var',
        }[r.data.durum]);
      } else {
        await api.patch(`/insaat/konumlar/${form.konum.id}`, { ad });
        toast.success('Ad güncellendi');
      }
      setForm(null);
      yukle();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
    setKaydediliyor(false);
  };

  const aktiflikDegistir = async (k) => {
    try {
      await api.patch(`/insaat/konumlar/${k.id}`, { is_active: !k.is_active });
      toast.success(k.is_active ? 'Pasif yapıldı — alt yüklenici formunda görünmeyecek' : 'Aktif yapıldı');
      setForm(null);
      yukle();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
  };

  const bolumEtiketleri = (bolumler) => bolumler.length > 0 && (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {bolumler.map((d) => (
        <button key={d.id} type="button" onClick={() => duzenle(d)}
          className={`text-xs px-2 py-0.5 rounded border hover:border-[#8a7e70]
            ${d.is_active ? 'bg-[#faf7f2] border-[#e8dfd0] text-[#3F3E39]' : 'bg-[#7a3535]/5 border-[#7a3535]/30 text-[#7a3535] line-through'}`}>
          {d.ad}
          {cocuklar(gorunen, d.id, 'mahal').length > 0 && (
            <span className="ml-1 text-[#8a7e70]">· {cocuklar(gorunen, d.id, 'mahal').length}</span>
          )}
        </button>
      ))}
    </div>
  );

  const blokKarti = (b) => {
    const katsizBolumler = cocuklar(gorunen, b.id, 'bolum');
    const katlar = cocuklar(gorunen, b.id, 'kat');
    const bolumSayisi = katsizBolumler.length
      + katlar.reduce((n, k) => n + cocuklar(gorunen, k.id, 'bolum').length, 0);
    return (
      <div key={b.id} className={`border border-[#d0c4b0] rounded-lg bg-white p-3 ${b.is_active ? '' : 'opacity-60'}`}
        data-testid={`konum-blok-${b.id}`}>
        {/* İki satır: "+ Kat" eklenince tek satırda blok adı "A…" diye kesiliyordu */}
        <div className="flex items-center justify-between gap-2">
          <button type="button" onClick={() => duzenle(b)} className="flex items-center gap-2 min-w-0 text-left group">
            <Building2 size={14} className="text-[#8a7e70] shrink-0" />
            <span className="font-semibold text-[#1a1916] truncate group-hover:underline">{b.ad}</span>
            {!b.is_active && pasifRozet}
          </button>
          <span className="text-xs text-[#8a7e70] shrink-0">
            {katlar.length > 0 && `${katlar.length} kat · `}{bolumSayisi} bölüm
          </span>
        </div>
        <div className="flex items-center gap-1 -ml-2">
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => ekle('kat', b.id)}
              title="Kat ekle" data-testid={`kat-ekle-${b.id}`}>
              <Plus size={12} className="mr-0.5" /> Kat
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => ekle('bolum', b.id)}
              title="Bloğa doğrudan bağımsız bölüm ekle">
              <Plus size={12} className="mr-0.5" /> Bölüm
            </Button>
          </div>
        </div>
        {bolumEtiketleri(katsizBolumler)}
        {katlar.map((k) => (
          <div key={k.id} className={`mt-2 pl-3 border-l-2 border-[#e8dfd0] ${k.is_active ? '' : 'opacity-60'}`}
            data-testid={`konum-kat-${k.id}`}>
            <div className="flex items-center justify-between gap-2">
              <button type="button" onClick={() => duzenle(k)} className="text-xs font-semibold text-[#3F3E39] hover:underline text-left">
                {k.ad} {!k.is_active && pasifRozet}
              </button>
              <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[11px]" onClick={() => ekle('bolum', k.id)}
                title="Bu kata bağımsız bölüm ekle">
                <Plus size={11} className="mr-0.5" /> Bölüm
              </Button>
            </div>
            {bolumEtiketleri(cocuklar(gorunen, k.id, 'bolum'))}
          </div>
        ))}
      </div>
    );
  };

  if (!site) {
    return <div className="p-8 text-center text-[#8a7e70]">Yükleniyor...</div>;
  }

  const etaplar = cocuklar(gorunen, null, 'etap');
  const kokBloklar = cocuklar(gorunen, null, 'blok');
  const ustAd = form?.parent_id ? konumlar.find((k) => k.id === form.parent_id)?.ad : null;

  return (
    <div data-testid="insaat-site-detail-page">
      <button onClick={() => navigate('/erp/insaat/sites')}
        className="flex items-center gap-1 text-sm text-[#8a7e70] hover:text-[#1a1916] mb-4">
        <ArrowLeft size={14} /> Şantiyelere Dön
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight flex items-center gap-2">
            <MapPin size={24} className="text-[#8a7e70]" /> {site.name}
          </h1>
          <p className="text-sm text-[#8a7e70] mt-1">
            {[site.code, site.address].filter(Boolean).join(' · ') || 'Adres girilmemiş'}
          </p>
          <p className="text-sm text-[#3F3E39] mt-1">
            {aktifSay('etap')} etap · {aktifSay('blok')} blok / bölge · {aktifSay('kat')} kat · {aktifSay('bolum')} bağımsız bölüm · {aktifSay('mahal')} mahal
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" className="border-[#d0c4b0]" onClick={() => ekle('etap')} data-testid="etap-ekle-btn">
            <Plus size={14} className="mr-1" /> Etap
          </Button>
          <Button variant="outline" className="border-[#d0c4b0]" onClick={() => ekle('blok')} data-testid="blok-ekle-btn">
            <Plus size={14} className="mr-1" /> Blok / Bölge
          </Button>
          <Button className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]" onClick={() => setExcelAcik(true)}
            data-testid="konum-excel-btn">
            <FileSpreadsheet size={14} className="mr-1" /> Excel'den Yükle
          </Button>
        </div>
      </div>

      {pasifSayisi > 0 && (
        <label className="flex items-center gap-2 text-sm text-[#3F3E39] mb-4 cursor-pointer w-fit">
          <Checkbox checked={pasifGoster} onCheckedChange={(v) => setPasifGoster(!!v)} />
          Pasifleri göster ({pasifSayisi})
        </label>
      )}

      {gorunen.length === 0 && (
        <div className="bg-[#faf7f2] border border-dashed border-[#d0c4b0] rounded-lg p-8 text-center text-sm text-[#8a7e70]">
          Henüz konum yok. Etap (opsiyonel), Blok / Bölge ve bağımsız bölümleri tek tek ekleyin
          ya da "Excel'den Yükle" ile toplu yükleyin. Otopark, peyzaj gibi ortak alanları da
          birer Blok / Bölge olarak açabilirsiniz.
        </div>
      )}

      <div className="space-y-4">
        {etaplar.map((e) => {
          const bloklar = cocuklar(gorunen, e.id, 'blok');
          return (
            <div key={e.id} className={`bg-[#faf7f2] border border-[#d0c4b0] rounded-lg p-4 ${e.is_active ? '' : 'opacity-60'}`}
              data-testid={`konum-etap-${e.id}`}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <Layers size={16} className="text-[#8a7e70]" />
                  <h2 className="text-lg font-bold text-[#1a1916]">{e.ad}</h2>
                  {!e.is_active && pasifRozet}
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => duzenle(e)} title="Düzenle">
                    <Pencil size={12} />
                  </Button>
                </div>
                <Button size="sm" variant="outline" className="border-[#d0c4b0] h-8" onClick={() => ekle('blok', e.id)}>
                  <Plus size={12} className="mr-1" /> Blok / Bölge
                </Button>
              </div>
              {bloklar.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">{bloklar.map(blokKarti)}</div>
              ) : (
                <p className="text-sm text-[#8a7e70]">Bu etapta blok / bölge yok.</p>
              )}
            </div>
          );
        })}

        {kokBloklar.length > 0 && (
          <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg p-4">
            <h2 className="text-lg font-bold text-[#1a1916] mb-3">
              {etaplar.length > 0 ? 'Etapsız Blok / Bölgeler' : 'Blok / Bölgeler'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">{kokBloklar.map(blokKarti)}</div>
          </div>
        )}
      </div>

      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-md">
          {form && (
            <form onSubmit={kaydet}>
              <DialogHeader>
                <DialogTitle className="text-[#1a1916] font-bold">
                  {KONUM_ADI[form.tur]} {form.mod === 'ekle' ? 'Ekle' : 'Düzenle'}
                </DialogTitle>
                <DialogDescription className="text-[#8a7e70] text-sm">
                  {form.mod === 'ekle'
                    ? (ustAd ? `${ustAd} altına eklenecek.` : 'Şantiyeye doğrudan eklenecek.')
                    : 'Ad değişikliği geçmiş raporları değiştirmez; raporlar kaydedildiği andaki adı tutar.'}
                  {form.mod === 'ekle' && (form.tur === 'bolum' || form.tur === 'mahal') && ' Çok sayıda kayıt için Excel\'den Yükle\'yi kullanın.'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 py-4">
                <Label className="text-[#3F3E39]">Ad *</Label>
                <Input autoFocus value={form.ad} onChange={(e) => setForm((f) => ({ ...f, ad: e.target.value }))}
                  placeholder={{ etap: '1. Etap', blok: 'A Blok, Otopark...', kat: 'Zemin Kat, 1. Kat, Çatı...', bolum: 'D1', mahal: 'Salon, Banyo, Teras...' }[form.tur]}
                  className="bg-white border-[#d0c4b0]" data-testid="konum-ad-input" />
              </div>
              {form.mod === 'duzenle' && form.tur === 'bolum' && (
                <div className="pb-4" data-testid="bolum-mahalleri">
                  <div className="flex items-center justify-between">
                    <Label className="text-[#3F3E39]">Mahaller ({cocuklar(gorunen, form.konum.id, 'mahal').length})</Label>
                    <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs"
                      onClick={() => ekle('mahal', form.konum.id)} data-testid="mahal-ekle-btn">
                      <Plus size={12} className="mr-0.5" /> Mahal
                    </Button>
                  </div>
                  {bolumEtiketleri(cocuklar(gorunen, form.konum.id, 'mahal'))}
                </div>
              )}
              <DialogFooter className="gap-2">
                {form.mod === 'duzenle' && (
                  <Button type="button" variant="outline" onClick={() => aktiflikDegistir(form.konum)}
                    className={`mr-auto ${form.konum.is_active ? 'border-[#7a3535]/40 text-[#7a3535]' : 'border-[#3d6e4e]/40 text-[#3d6e4e]'}`}>
                    {form.konum.is_active ? 'Pasif Yap' : 'Aktif Yap'}
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={() => setForm(null)} className="border-[#d0c4b0]">İptal</Button>
                <Button type="submit" disabled={kaydediliyor || !form.ad.trim()}
                  className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]" data-testid="konum-kaydet-btn">
                  {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <ImportDialog open={excelAcik} onOpenChange={setExcelAcik} config={{
        title: `${site.name} — Konumları Excel'den Yükle`,
        templateUrl: `/insaat/sites/${id}/konumlar/sablon`,
        previewUrl: `/insaat/sites/${id}/konumlar/onizle`,
        executeUrl: `/insaat/sites/${id}/konumlar/aktar`,
        columns: IMPORT_SUTUNLARI,
        onComplete: yukle,
      }} />
    </div>
  );
}
